import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faTag,
  faTimes,
  faBookmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { IconPicker, PREDEFINED_ICONS } from "./IconPicker";
import { motion } from "framer-motion";
import ReactDOM from "react-dom";
import {
  resolveColor,
  getDarkModeColor,
  darkenHexColor,
  getSecondaryColor,
  rgbToHsl,
  hslToRgb,
  luminance,
  getContrastTextColor,
} from "@/utils/colorUtils";

// Global state to track open popover
let openPopoverId: string | null = null;
const openPopoverListeners = new Set<() => void>();

const notifyPopoverListeners = () => {
  openPopoverListeners.forEach((listener) => listener());
};

interface EditablePillProps {
  text: string;
  currentIcon?: string;
  currentColor?: string;
  onIconChange: (iconName: string | undefined) => void;
  onColorChange: (colorHex: string | undefined) => void;
  onClickPill?: () => void;
  className?: string;
  isActive?: boolean;
  disableIconEdit?: boolean;
  icon?: React.ReactNode;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  isNew?: boolean;
  isOverlay?: boolean;
  style?: React.CSSProperties;
  useGradient?: boolean;
  opacityIntensity?: number;
  animateBorder?: boolean;
  fluid?: boolean;
  gradientColors?: string[];
  flat?: boolean;
  flatOpacity?: number;
  showNotification?: boolean;
  count?: number;
  /** Force dark text color, bypassing auto-contrast calculation */
  forceTextDark?: boolean;
  /** Hide the border completely */
  hideBorder?: boolean;
}

// Helper to get IconDefinition from name
const getIconDefinitionByName = (name?: string): IconDefinition | undefined => {
  if (!name) return undefined;
  const found = PREDEFINED_ICONS.find((icon) => icon.name === name);
  return found?.definition;
};

// Helper to generate a hash number from a string
function hashStringToNumber(str: string, min: number, max: number): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return (Math.abs(hash) % (max - min + 1)) + min;
}

function isHexColor(hex: string): boolean {
  if (!hex || typeof hex !== "string") return false;
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(hex.trim());
}

// Helper to blend two hex colors by alpha
function blendHexColors(fgHex: string, bgHex: string, alpha: number): string {
  fgHex = fgHex.replace("#", "");
  bgHex = bgHex.replace("#", "");
  const fr = parseInt(fgHex.substring(0, 2), 16);
  const fg = parseInt(fgHex.substring(2, 4), 16);
  const fb = parseInt(fgHex.substring(4, 6), 16);
  const br = parseInt(bgHex.substring(0, 2), 16);
  const bg = parseInt(bgHex.substring(2, 4), 16);
  const bb = parseInt(bgHex.substring(4, 6), 16);
  const r = Math.round(fr * alpha + br * (1 - alpha));
  const g = Math.round(fg * alpha + bg * (1 - alpha));
  const b = Math.round(fb * alpha + bb * (1 - alpha));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function hexToRgba(hex: string, alpha: number): string {
  if (!isHexColor(hex)) return `rgba(229, 231, 235, ${alpha})`; // fallback to slate-200
  hex = hex.replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Helper to calculate contrast ratio
function contrastRatio(hex1: string, hex2: string): number {
  const l1 = luminance(hex1);
  const l2 = luminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function EditablePill({
  text,
  count,
  currentIcon,
  currentColor,
  onIconChange,
  onColorChange,
  onClickPill,
  className = "",
  isActive = false,
  disableIconEdit = false,
  icon,
  showRemoveButton = false,
  onRemove,
  isNew,
  isOverlay = false,
  style,
  useGradient = true,
  opacityIntensity = 0.3,
  animateBorder = false,
  fluid = false,
  gradientColors = ["#c084fc", "#a7bffa", "#5af4ff"],
  flat = false,
  flatOpacity = 0.8,
  showNotification = false,
  forceTextDark = false,
  hideBorder = false,
}: EditablePillProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isColorPopoverOpen, setIsColorPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const pillRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLSpanElement | null>(null);
  const ignoreNextClick = useRef(false);
  const pillId = useRef(`pill-${Math.random().toString(36).substr(2, 9)}`);

  let isIconExplicitlySuppressed = false;
  if (icon === null) {
    isIconExplicitlySuppressed = true;
  } else if (React.isValidElement(icon) && icon.type === React.Fragment) {
    const fragmentProps = icon.props as
      | { children?: React.ReactNode | React.ReactNode[] }
      | null
      | undefined;

    if (
      !fragmentProps ||
      !fragmentProps.children ||
      (Array.isArray(fragmentProps.children) &&
        fragmentProps.children.length === 0)
    ) {
      isIconExplicitlySuppressed = true;
    }
  }
  const showIconContainer = !isIconExplicitlySuppressed;

  const updatePopoverPosition = useCallback(() => {
    if (iconRef.current && popoverRef.current) {
      const iconRect = iconRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      // Calculate position
      let top = iconRect.bottom + window.scrollY + 8; // 8px offset
      let left = iconRect.left + window.scrollX;

      // Ensure popover stays within viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Adjust horizontal position if needed
      if (left + popoverRect.width > viewportWidth) {
        left = viewportWidth - popoverRect.width - 8;
      }

      // Adjust vertical position if needed
      if (top + popoverRect.height > viewportHeight + window.scrollY) {
        top = iconRect.top + window.scrollY - popoverRect.height - 8;
      }

      setPopoverPosition({ top, left });
    }
  }, []);

  useEffect(() => {
    if (isPopoverOpen) {
      updatePopoverPosition();

      const handleScroll = () => {
        updatePopoverPosition();
      };

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isPopoverOpen, updatePopoverPosition]);

  useEffect(() => {
    const handleOtherPopoverOpen = () => {
      if (openPopoverId !== pillId.current) {
        setIsPopoverOpen(false);
      }
    };
    openPopoverListeners.add(handleOtherPopoverOpen);
    return () => {
      openPopoverListeners.delete(handleOtherPopoverOpen);
    };
  }, []);

  const selectedIconDef = getIconDefinitionByName(currentIcon) || faTag;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isPopoverOpen) return;
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }
      const target = event.target as HTMLElement;
      if (target.closest("[data-color-picker-portal]")) {
        return;
      }
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        pillRef.current &&
        !pillRef.current.contains(event.target as Node)
      ) {
        setIsPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopoverOpen]);

  const handleIconSelect = (iconName: string) => {
    onIconChange(iconName);
  };

  const handleColorSelect = (colorHex: string) => {
    onColorChange(colorHex);
  };

  // At the top of the EditablePill component, after props:
  const isDarkMode =
    typeof window !== "undefined" &&
    document.documentElement.classList.contains("dark");
  const resolvedHex = resolveColor(currentColor);
  const baseColor = resolvedHex
    ? isDarkMode
      ? getDarkModeColor(resolvedHex)
      : resolvedHex
    : "#e5e7eb";
  const gradientAngle = useMemo(
    () => hashStringToNumber(text, 90, 270),
    [text]
  );
  const gradientStart = darkenHexColor(baseColor, -0.1);
  const gradientMid = baseColor;
  const gradientEnd = getSecondaryColor(baseColor);

  // Add this helper to determine if baseColor is very light or default
  const isBaseColorLight =
    !baseColor || baseColor === "#e5e7eb" || luminance(baseColor) > 0.9;

  // For randomized conic gradient stops (gray fallback)
  const conicStop1 = useMemo(() => hashStringToNumber(text, 0, 60), [text]);
  const conicStop2 = useMemo(() => hashStringToNumber(text, 80, 160), [text]);
  const conicStop3 = useMemo(() => hashStringToNumber(text, 180, 300), [text]);
  const conicStop4 = 360;

  const initialAngle = useMemo(() => hashStringToNumber(text, 90, 270), [text]);

  const borderStyle = useMemo(() => {
    const fromAngle = animateBorder
      ? `calc(var(--border-angle) + ${initialAngle}deg)`
      : `${initialAngle}deg`;

    let background;
    const borderBaseColor = fluid ? gradientColors[0] : currentColor;

    if (animateBorder) {
      if (borderBaseColor) {
        if (flat) {
          // For flat variation with animate border, use single color fading to transparent
          background = `conic-gradient(
            from ${fromAngle},
            transparent 0deg,
            ${hexToRgba(borderBaseColor, 0.3)} 30deg,
            ${borderBaseColor} 75deg,
            ${borderBaseColor} 135deg,
            ${hexToRgba(borderBaseColor, 0.3)} 180deg,
            transparent 225deg
          )`;
        } else {
          background = `conic-gradient(
            from ${fromAngle},
            transparent 0deg,
            ${darkenHexColor(borderBaseColor, -0.1)} 45deg,
            ${borderBaseColor} 120deg,
            ${getSecondaryColor(borderBaseColor)} 240deg,
            transparent 315deg
          )`;
        }
      } else {
        if (flat) {
          // For flat variation without color, use gray fading to transparent
          background = `conic-gradient(
            from ${fromAngle},
            transparent 0deg,
            rgba(156, 163, 175, 0.3) 30deg,
            #9ca3af 75deg,
            #9ca3af 135deg,
            rgba(156, 163, 175, 0.3) 180deg,
            transparent 225deg
          )`;
        } else {
          background = `conic-gradient(
            from ${fromAngle},
            transparent 0deg,
            #9ca3af 45deg,
            #4b5563 ${conicStop2}deg,
            #374151 ${conicStop3}deg,
            transparent 315deg
          )`;
        }
      }
    } else {
      // Original static gradient
      if (borderBaseColor) {
        if (flat) {
          // For flat variation without animation, use single color with transparent sections
          background = `conic-gradient(
            from ${fromAngle},
            ${borderBaseColor} 0deg,
            ${borderBaseColor} 120deg,
            transparent 180deg,
            transparent 300deg,
            ${borderBaseColor} 360deg
          )`;
        } else {
          background = `conic-gradient(
            from ${fromAngle},
            ${darkenHexColor(borderBaseColor, -0.1)} 0deg,
            ${borderBaseColor} 120deg,
            ${getSecondaryColor(borderBaseColor)} 240deg,
            transparent 300deg,
            ${darkenHexColor(borderBaseColor, -0.1)} 360deg
          )`;
        }
      } else {
        if (flat) {
          // For flat variation without color, use gray with transparent sections
          background = `conic-gradient(
            from ${fromAngle},
            #9ca3af 0deg,
            #9ca3af 120deg,
            transparent 180deg,
            transparent 300deg,
            #9ca3af 360deg
          )`;
        } else {
          background = `conic-gradient(
            from ${fromAngle},
            #9ca3af ${conicStop1}deg,
            #4b5563 ${conicStop2}deg,
            #374151 ${conicStop3}deg,
            transparent 300deg,
            #9ca3af 360deg
          )`;
        }
      }
    }

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      inset: -4, // Offset by 1px to create the border effect
      zIndex: 0,
      mixBlendMode: "screen",
      borderRadius: "1rem", // Slightly larger to account for the 4px offset
      padding: 2, // border width
      pointerEvents: "none",
      opacity: 0.5,
      background,
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
      maskComposite: "exclude",
    };

    if (animateBorder) {
      return {
        ...baseStyle,
        animation: "border-spin-ani 3s ease-in-out infinite",
      };
    }

    return baseStyle;
  }, [
    animateBorder,
    currentColor,
    initialAngle,
    conicStop1,
    conicStop2,
    conicStop3,
    fluid,
    gradientColors,
    flat,
  ]);

  const pillStyle = useMemo((): React.CSSProperties => {
    if (fluid) {
      const opacity = Math.max(0, Math.min(1, opacityIntensity));
      const rgbaColors = gradientColors
        .map((color) => hexToRgba(color, opacity))
        .join(", ");
      const backgroundStyle = `linear-gradient(to right, ${rgbaColors[0]})`;

      return {
        background: backgroundStyle,
        borderRadius: "0.75rem",
        position: "relative",
        zIndex: 1,
        transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
      };
    }

    if (!resolvedHex) {
      // Flat tabs/pills without a custom color — surface comes from Tailwind in pillClasses.
      if (flat) {
        return {
          borderRadius: "0.75rem",
          position: "relative",
          zIndex: 1,
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          border: "none",
        };
      }
      return {};
    }

    let backgroundStyle: string;
    if (flat && baseColor) {
      // Only use flatOpacity if there's a specified color and it's not the default
      const isDefaultColor =
        !currentColor || baseColor === "#e5e7eb" || baseColor === "#314158";
      const opacity = isDefaultColor
        ? 1
        : Math.max(0, Math.min(1, flatOpacity));

      if (opacity === 1) {
        backgroundStyle = baseColor;
      } else {
        // Blend with background color instead of using transparency
        const backgroundColor = isDarkMode ? "#1f2937" : "#ffffff"; // slate-800 for dark, white for light
        backgroundStyle = blendHexColors(baseColor, backgroundColor, opacity);
      }
    } else if (useGradient && baseColor) {
      const opacity = Math.max(0, Math.min(1, opacityIntensity));
      backgroundStyle = `linear-gradient(${gradientAngle}deg, 
        color-mix(in srgb, ${gradientStart} ${opacity * 100}%, transparent) 0%, 
        color-mix(in srgb, ${gradientMid} ${opacity * 100}%, transparent) 50%, 
        color-mix(in srgb, ${gradientEnd} ${
          opacity * 100
        }%, transparent) 100%)`;
    } else if (baseColor) {
      const opacity = Math.max(0, Math.min(1, opacityIntensity));
      backgroundStyle = `color-mix(in srgb, ${baseColor} ${
        opacity * 100
      }%, transparent)`;
    } else {
      backgroundStyle = "";
    }

    return {
      background: backgroundStyle,
      borderRadius: "0.75rem",
      position: "relative",
      zIndex: 1,
      transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
      ...(flat && { border: "none" }), // Ensure no border when flat is true
    };
  }, [
    resolvedHex,
    useGradient,
    flat,
    opacityIntensity,
    text,
    baseColor,
    gradientAngle,
    gradientStart,
    gradientMid,
    gradientEnd,
    fluid,
    gradientColors,
    flatOpacity,
  ]);

  const activeTransform = useMemo(
    () => (isActive ? { transform: "translateY(-10px)" } : {}),
    [isActive]
  );

  const fluidTextStyle = useMemo(() => {
    if (!fluid) return {};
    return {
      background: `linear-gradient(to right, ${gradientColors.join(", ")})`,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      color: "transparent",
      WebkitTextFillColor: "transparent",
      display: "inline-block",
    };
  }, [fluid, gradientColors]);

  let textColorClassName = "text-slate-700 dark:text-slate-300";
  if (forceTextDark) {
    // Force dark text, bypassing auto-contrast
    textColorClassName = "text-slate-800 dark:text-slate-900";
  } else if (currentColor && !fluid) {
    const bgColor = isDarkMode ? getDarkModeColor(currentColor) : currentColor;
    // For flat case, use flatOpacity for contrast calculation
    // For non-flat case, use opacityIntensity for contrast calculation
    const opacity = flat
      ? Math.max(0, Math.min(1, flatOpacity))
      : Math.max(0, Math.min(1, opacityIntensity));
    textColorClassName = getContrastTextColor(bgColor, opacity, isDarkMode);
  }
  const textClasses = `${
    showIconContainer ? "pr-3" : "px-3"
  } ${textColorClassName}`;

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Only handle icon clicks if icon editing is enabled
    if (disableIconEdit) {
      return;
    }

    ignoreNextClick.current = true;
    console.log("Icon clicked", { text, isPopoverOpen, shouldShowPopover });
    if (!isPopoverOpen) {
      notifyPopoverListeners();
      openPopoverId = pillId.current;
      setPendingOpen(true);
    } else {
      setIsPopoverOpen(false);
      openPopoverId = null;
    }
  };

  const handlePillClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".popover-content")) return;
    const targetNode = e.target as Node;
    if (iconRef.current && iconRef.current.contains(targetNode)) return;
    if ((targetNode as HTMLElement).closest('button[aria-label="Remove"]'))
      return;
    if (isPopoverOpen || isColorPopoverOpen) return;
    if (onClickPill) {
      onClickPill();
    }
  };

  const [shouldShowPopover, setShouldShowPopover] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  useLayoutEffect(() => {
    if (pendingOpen && iconRef.current) {
      setShouldShowPopover(true);
      setIsPopoverOpen(true);
      setPendingOpen(false);
      if (typeof updatePopoverPosition === "function") updatePopoverPosition();
    }
  }, [pendingOpen, iconRef.current, updatePopoverPosition]);

  useEffect(() => {
    if (!isPopoverOpen) {
      setShouldShowPopover(false);
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    if (isPopoverOpen) {
      openPopoverId = pillId.current;
      notifyPopoverListeners();
    } else if (openPopoverId === pillId.current) {
      openPopoverId = null;
    }
  }, [isPopoverOpen]);

  // Reset popover states when icon editing is disabled
  useEffect(() => {
    if (disableIconEdit) {
      setIsPopoverOpen(false);
      setIsColorPopoverOpen(false);
      if (openPopoverId === pillId.current) {
        openPopoverId = null;
        notifyPopoverListeners();
      }
    }
  }, [disableIconEdit]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      if (openPopoverId === pillId.current) {
        openPopoverId = null;
        notifyPopoverListeners();
      }
    };
  }, []);

  useEffect(() => {
    if (!isPopoverOpen && openPopoverId === pillId.current) {
      openPopoverId = null;
    }
  }, [isPopoverOpen]);

  const indicatorColor = isDarkMode
    ? currentColor
      ? getDarkModeColor(currentColor)
      : "#e5e7eb"
    : currentColor
      ? `${currentColor}50`
      : "#e5e7eb";

  const pillClasses = `flex items-center relative ${
    !flat
      ? "after:absolute after:inset-0 after:rounded-xl after:ring-inset after:ring-white/30"
      : ""
  } relative rounded-xl px-1 text-sm font-medium h-9 focus:outline-none ${
    isOverlay ? "" : "cursor-pointer"
  } ${
    !currentColor
      ? flat
        ? "bg-slate-200 text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
        : "text-slate-800 hover:bg-slate-200 dark:text-white dark:bg-slate-700 dark:hover:bg-slate-600"
      : flat
        ? "hover:brightness-110"
        : "hover:brightness-110"
  } [&>*]:relative [&>*]:z-10 [&>*]:isolate`;

  return (
    <div
      className={`!relative inline-block w-fit align-middle ${className}`}
      style={{ minHeight: 48, ...style }}
    >
      <div
        className={`absolute z-10  border-2 border-slate-800 transition-opacity duration-200 bg-red-400 text-white text-xs rounded-full w-4 h-4 ${
          showNotification ? "opacity-100" : "opacity-0"
        } ${isActive ? "-top-4 -right-1.5" : "-top-1.5 -right-1.5"}`}
      ></div>

      {/* Gradient border span */}

      {/* Button content on top */}
      {showRemoveButton ? (
        <div
          ref={pillRef as React.RefObject<HTMLDivElement>}
          onClick={handlePillClick}
          className={pillClasses}
          style={{ ...pillStyle, ...activeTransform }}
        >
          {showIconContainer && (
            <span
              ref={iconRef}
              className="flex items-center justify-center w-7 h-7 rounded-full mr-1 ml-0.5 transition-colors icon-container"
              onPointerDown={(e) => {
                // Prevent DND from starting a drag when clicking the icon
                e.stopPropagation();
              }}
              onClick={disableIconEdit ? undefined : handleIconClick}
              role={disableIconEdit ? undefined : "button"}
              tabIndex={disableIconEdit ? -1 : 0}
              aria-haspopup={disableIconEdit ? "true" : undefined}
              aria-expanded={isPopoverOpen}
              title={
                disableIconEdit
                  ? undefined
                  : currentIcon
                    ? `Change icon (${currentIcon})`
                    : "Choose icon"
              }
              style={{ color: currentColor || undefined }}
            >
              {icon && !isIconExplicitlySuppressed ? (
                icon
              ) : currentIcon && !getIconDefinitionByName(currentIcon) ? (
                <span className="text-sm">{currentIcon}</span>
              ) : (
                <FontAwesomeIcon
                  key={currentIcon || "defaultFaIcon"}
                  icon={selectedIconDef}
                  className="transition-colors"
                  style={{ color: currentColor || undefined }}
                />
              )}
            </span>
          )}
          <span className={textClasses} style={fluidTextStyle}>
            {text}
          </span>
          {count !== undefined && count > 0 && (
            <span className="ml-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-900/10 dark:bg-slate-100/10 px-2 py-0.5 rounded-full">
              {count}
            </span>
          )}
          {showRemoveButton && onRemove && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove();
              }}
              className="flex items-center justify-center mr-1 w-5 h-5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-100"
              aria-label="Remove"
              style={{ padding: 0 }}
            >
              <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
            </button>
          )}
        </div>
      ) : (
        <button
          ref={pillRef as React.RefObject<HTMLButtonElement>}
          onClick={handlePillClick}
          className={pillClasses}
          style={{ ...pillStyle, ...activeTransform }}
          onMouseEnter={undefined}
          onMouseLeave={undefined}
        >
          {animateBorder && (
            <style>{`
              @property --border-angle {
                syntax: '<angle>';
                inherits: false;
                initial-value: 0turn;
              }
              @keyframes border-spin-ani {
                from {
                  --border-angle: 0turn;
                }
                to {
                  --border-angle: 1turn;
                }
              }
            `}</style>
          )}
          {!hideBorder && !flat && (
            <span aria-hidden="true" style={borderStyle} />
          )}
          {!hideBorder && flat && animateBorder && (
            <span aria-hidden="true" style={borderStyle} />
          )}
          {showIconContainer && (
            <span
              ref={iconRef}
              className={`flex items-center justify-center w-7 h-7 rounded-full mr-1 ml-0.5 transition-colors icon-container ${
                disableIconEdit ? "" : "hover:bg-slate-900/20"
              }`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={disableIconEdit ? undefined : handleIconClick}
              role={disableIconEdit ? undefined : "button"}
              tabIndex={disableIconEdit ? -1 : 0}
              aria-haspopup={disableIconEdit ? "true" : undefined}
              aria-expanded={isPopoverOpen}
              title={
                disableIconEdit
                  ? undefined
                  : currentIcon
                    ? `Change icon (${currentIcon})`
                    : "Choose icon"
              }
              style={{ color: currentColor || undefined }}
            >
              {icon && !isIconExplicitlySuppressed ? (
                icon
              ) : currentIcon && !getIconDefinitionByName(currentIcon) ? (
                <span className="text-sm">{currentIcon}</span>
              ) : (
                <FontAwesomeIcon
                  key={currentIcon || "defaultFaIcon"}
                  icon={selectedIconDef}
                  className="transition-colors"
                  style={{ color: currentColor || undefined }}
                />
              )}
            </span>
          )}
          <span className={textClasses} style={fluidTextStyle}>
            {text}
          </span>
          {count !== undefined && count > 0 && (
            <span className="mr-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-900/10 dark:bg-slate-100/10 px-2 py-0.5 rounded-full">
              <div className="top-[1px] relative">{count}</div>
            </span>
          )}
        </button>
      )}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 h-[3px] rounded-full pointer-events-none"
        style={{
          background:
            isActive && !isBaseColorLight
              ? flat
                ? hexToRgba(baseColor, Math.max(0, Math.min(1, flatOpacity))) // Use flatOpacity for flat pills
                : `linear-gradient(${gradientAngle}deg, 
                    ${gradientStart} 0%, 
                    ${gradientMid} 50%, 
                    ${gradientEnd} 100%)`
              : isActive
                ? flat
                  ? hexToRgba("#9ca3af", Math.max(0, Math.min(1, flatOpacity))) // Use flatOpacity for flat pills with light colors
                  : `linear-gradient(${gradientAngle}deg, #9ca3af 0%, #9ca3af80 100%)`
                : "transparent",
          width: isActive ? "50%" : "10%",
          opacity: isActive ? 1 : 0,
          bottom: 12,
          transition: "background 0.2s",
        }}
        initial={false}
        animate={{
          width: isActive ? "50%" : "10%",
          opacity: isActive ? 1 : 0,
          background: isActive
            ? !isBaseColorLight
              ? flat
                ? hexToRgba(baseColor, Math.max(0, Math.min(1, flatOpacity))) // Use flatOpacity for flat pills
                : `linear-gradient(${gradientAngle}deg, 
                    ${gradientStart} 0%, 
                    ${gradientMid} 50%, 
                    ${gradientEnd} 100%)`
              : flat
                ? hexToRgba("#9ca3af", Math.max(0, Math.min(1, flatOpacity))) // Use flatOpacity for flat pills with light colors
                : `linear-gradient(${gradientAngle}deg, #9ca3af 0%, #9ca3af80 100%)`
            : "transparent",
        }}
        transition={{
          duration: 0.15,
          ease: "easeInOut",
          delay: isActive ? 0.05 : 0,
        }}
      />
      {!disableIconEdit &&
        showIconContainer &&
        shouldShowPopover &&
        ReactDOM.createPortal(
          <motion.div
            ref={popoverRef}
            className="z-[9999] w-auto bg-white min-w-64 shadow-xl dark:bg-slate-900 rounded-xl p-3 pb-0 popover-content"
            style={{
              position: "absolute",
              top: popoverPosition.top,
              left: popoverPosition.left,
              minWidth: 220,
              visibility: isPopoverOpen ? "visible" : "hidden",
              pointerEvents: isPopoverOpen ? "auto" : "none",
            }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{
              opacity: isPopoverOpen ? 1 : 0,
              scale: isPopoverOpen ? (isColorPopoverOpen ? 0.95 : 1) : 0.95,
            }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            {isPopoverOpen && (
              <div
                className="flex flex-col gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <div>
                  <IconPicker
                    onSelectIcon={handleIconSelect}
                    currentIcon={currentIcon}
                    currentColor={currentColor}
                    onSelectColor={handleColorSelect}
                    onColorPopoverOpenChange={setIsColorPopoverOpen}
                  />
                </div>
              </div>
            )}
          </motion.div>,
          document.body
        )}
    </div>
  );
}
